import {useRef, useState} from 'react';

import {arNum, ASPECT_CSS, SOURCE_LABEL} from '../format';
import {assetUrl, isVideoAsset, useStudio} from '../store';
import type {Aspect, Scene, SceneView} from '../types';

import {Elapsed} from './Elapsed';
import {ArchiveBox, PlaceholderFrame, Regenerate, UploadArrow} from './Icons';

/**
 * A generated visual is referenced by the provider's URL, and those expire. So a load
 * failure is expected eventually and is reported as itself, with the reason, instead of
 * leaving a broken-image box that looks like the app is at fault.
 */
function VisualMedia({scene, url, onFail}: {scene: Scene; url: string; onFail(): void}) {
  return isVideoAsset(scene.visual) ? (
    <video src={url} controls muted playsInline preload="metadata" onError={onFail} />
  ) : (
    <img src={url} alt={scene.visual?.prompt ?? ''} onError={onFail} />
  );
}

export function VisualColumn({
  scene,
  view,
  aspect,
}: {
  scene: Scene;
  view: SceneView;
  aspect: Aspect;
}) {
  const s = useStudio();
  const fileInput = useRef<HTMLInputElement>(null);
  const [loadFailed, setLoadFailed] = useState('');

  // A new visual deserves a fresh attempt at loading it.
  const currentKey = scene.visual?.s3_key || scene.visual?.url || '';
  const failedKey = useRef('');
  if (failedKey.current && failedKey.current !== currentKey) {
    failedKey.current = '';
    if (loadFailed) setLoadFailed('');
  }

  const busy = view.visual === 'busy';
  const showFailure = view.visual === 'fail' || !!loadFailed;
  const source = scene.visual?.source;

  let inner: React.ReactNode;
  if (busy) {
    // Gold, because this is the model working and nothing here is confirmed yet.
    // Elapsed time and an indeterminate sweep — fal.ai reports no progress, so
    // there is no percentage to show and inventing one would be a lie.
    inner = (
      <div className="vis-state">
        <span className="spin" style={{width: 32, height: 32, borderWidth: 2.5}} />
        <span>جارٍ توليد المرئيات</span>
        <span className="mono" style={{fontSize: '0.625rem'}}>
          <Elapsed />
        </span>
        <div className="indet gold" style={{width: '72%'}}>
          <div />
        </div>
      </div>
    );
  } else if (showFailure) {
    inner = (
      <div className="vis-state">
        <span className="badmark">!</span>
        <span className="err-note">
          {loadFailed ? 'تعذّر تحميل المشهد المرئي' : 'فشل توليد المشهد المرئي'}
        </span>
        <span style={{color: 'var(--muted)'}}>{loadFailed || view.visualError}</span>
        <button
          type="button"
          className="btn-out xs danger"
          onClick={() => void s.generateVisual(scene.id)}
        >
          أعد المحاولة
        </button>
        <button type="button" className="btn-txt" onClick={() => fileInput.current?.click()}>
          ارفع صورة
        </button>
      </div>
    );
  } else if (view.visual === 'ready') {
    inner = (
      <>
        <VisualMedia
          scene={scene}
          url={view.visualUrl}
          onFail={() => {
            failedKey.current = currentKey;
            setLoadFailed('رابط المزوّد ينتهي بعد فترة — أعد التوليد أو اختر بديلًا');
          }}
        />
        <div className="vis-acts">
          <button
            type="button"
            aria-label="من الأرشيف"
            title="اختر الأنسب من أرشيف المؤسسة"
            onClick={() => void s.archiveVisual(scene.id)}
          >
            <ArchiveBox />
          </button>
          <button
            type="button"
            aria-label="أعد التوليد"
            title="أعد التوليد"
            onClick={() => void s.generateVisual(scene.id)}
          >
            <Regenerate />
          </button>
        </div>
      </>
    );
  } else {
    inner = (
      <div className="vis-state">
        <PlaceholderFrame />
        <span>لم يُعثر على مشهد مناسب بالأرشيف</span>
        <button
          type="button"
          className="btn-out xs"
          onClick={() => void s.generateVisual(scene.id)}
        >
          ولّد بالذكاء الاصطناعي
        </button>
        <button type="button" className="btn-txt" onClick={() => fileInput.current?.click()}>
          ارفع ملف
        </button>
      </div>
    );
  }

  return (
    <div className="vis-col">
      <div className="vis-inner">
        <div className="blk-head">
          <span className="field-lbl">المشهد المرئي</span>
          {scene.visual && (
            <span className={`chip ${source === 'generated' ? 'ai' : 'draft'}`}>
              {SOURCE_LABEL[scene.visual.source]}
            </span>
          )}
        </div>

        {/* Source picker. These are actions, not passive tabs — the highlighted one
            is where the current visual came from. */}
        <div className="vis-tabs" role="group" aria-label="مصدر المشهد المرئي">
          <button
            type="button"
            className={source === 'archive' ? 'on' : ''}
            disabled={busy}
            title="اختر الأنسب من أرشيف المؤسسة"
            onClick={() => void s.archiveVisual(scene.id)}
          >
            أرشيف
          </button>
          <button
            type="button"
            className={source === 'upload' ? 'on' : ''}
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            رفع
          </button>
          <button
            type="button"
            className={source === 'generated' ? 'on ai' : ''}
            disabled={busy}
            onClick={() => void s.generateVisual(scene.id)}
          >
            ذكي
          </button>
        </div>

        <div
          className={`vis-box ${showFailure ? 'fail' : view.visual}`}
          style={{aspectRatio: ASPECT_CSS[aspect]}}
        >
          {inner}
        </div>

        {scene.alternatives.length > 0 && (
          <span className="field-lbl">بدائل ({arNum(scene.alternatives.length)})</span>
        )}

        <div className="alts">
          {scene.alternatives.map((alt, index) => {
            // Only a still can be a tile background; a video alternative keeps the plain
            // tile and is identified by its number and source.
            const thumb = isVideoAsset(alt) ? '' : assetUrl(alt);
            return (
              <button
                type="button"
                className="alt"
                key={`${alt.s3_key}${alt.url}${index}`}
                title={alt.prompt}
                disabled={busy}
                onClick={() => void s.pickAlternative(scene.id, index)}
                style={thumb ? {backgroundImage: `url('${thumb}')`} : undefined}
              >
                <b>{arNum(index + 1)}</b>
                <span>{SOURCE_LABEL[alt.source]}</span>
              </button>
            );
          })}
          <button
            type="button"
            className="alt up"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            <UploadArrow />
            <span>ارفع</span>
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset so re-picking the same file fires change again.
              e.target.value = '';
              if (file) void s.uploadVisual(scene.id, file);
            }}
          />
        </div>
      </div>
    </div>
  );
}
